/* To create a new Icon, you need to export the SVG from Figma, then parse
 * the "width", "height", and "path" from the SVG and paste these in a new
 * Icon function, passing them down to the InternalIcon function.
 *
 * Note: to export a new item, you can right click an element in Figma and
 * select "Copy/Paste as" -> "Copy as SVG". Then, paste the result in here
 * and extract the SVG page
 */
import CloseFullscreenSharpIcon from "@mui/icons-material/CloseFullscreenSharp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInFullSharpIcon from "@mui/icons-material/OpenInFullSharp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CSSProperties } from "react";

interface IconProps {
    color?: string;
    onClick?: (e: any) => void;
    scale?: number;
}
interface InternalIconProps {
    color?: string;
    onClick?: (e: any) => void;
    path: string;
    width: number;
    height: number;
    scale?: number;
}
function Icon({ color, onClick, path, width, height, scale }: InternalIconProps) {
    // eslint-disable-next-line no-param-reassign
    if (scale === undefined) scale = 1;

    const svgStyle: any = {
        fill: "var(--audette-black)",
    };
    const divStyle: any = {
        lineHeight: `${height * scale}px`,
    };

    if (onClick !== undefined) divStyle.cursor = "pointer";

    if (color !== undefined) svgStyle.fill = color;

    return (
        <button type="button" style={divStyle} onClick={onClick} aria-label="icon">
            <svg
                width={width * scale}
                height={height * scale}
                viewBox={`0 0 ${width} ${height}`}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path style={svgStyle} d={path} />
            </svg>
        </button>
    );
}

export function LeftAngleBracket({ color, onClick, scale }: IconProps) {
    const width = 12;
    const height = 18;

    const path =
        "M11.9693 2.11605V0.304334C11.9693 0.147303 11.7888 0.0605842 11.6669 0.156678L1.1013 8.40902C1.01153 8.47883 0.938895 8.56823 0.888929 8.67038C0.838962 8.77254 0.812988 8.88476 0.812988 8.99848C0.812988 9.1122 0.838962 9.22441 0.888929 9.32657C0.938895 9.42872 1.01153 9.51812 1.1013 9.58793L11.6669 17.8403C11.7911 17.9364 11.9693 17.8496 11.9693 17.6926V15.8809C11.9693 15.7661 11.9154 15.6559 11.8263 15.5856L3.3888 8.99965L11.8263 2.41137C11.9154 2.34105 11.9693 2.2309 11.9693 2.11605Z";

    return <Icon scale={scale} width={width} height={height} color={color} onClick={onClick} path={path} />;
}

export function CloseButton({ id, onClick }: { id?: string; onClick: (e: any) => void }) {
    return (
        <button
            id={id}
            type="button"
            onClick={onClick}
            style={{ display: "grid", placeItems: "center" }}
            aria-label="closeButton"
        >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M7.25143 6.17627L12 10.9248V12.1763H10.7486L6 7.4277L1.25143 12.1763H0V10.9248L4.74857 6.17627L0 1.4277V0.17627H1.25143L6 4.92484L10.7486 0.17627H12V1.4277L7.25143 6.17627Z"
                    fill="#545454"
                />
            </svg>
        </button>
    );
}

export function CloseIcon({
    color = "#000",
    width = 12,
    height = 12,
}: {
    color?: string;
    width?: number;
    height?: number;
}) {
    return (
        <svg width={width} height={height} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M7.25143 6.17627L12 10.9248V12.1763H10.7486L6 7.4277L1.25143 12.1763H0V10.9248L4.74857 6.17627L0 1.4277V0.17627H1.25143L6 4.92484L10.7486 0.17627H12V1.4277L7.25143 6.17627Z"
                fill={color}
            />
        </svg>
    );
}

export function Delete({ color = "#CC303C" }: { color?: string }) {
    return (
        <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M5.5 0.5V1.5H0.5V3.5H1.5V16.5C1.5 17.0304 1.71071 17.5391 2.08579 17.9142C2.46086 18.2893 2.96957 18.5 3.5 18.5H13.5C14.0304 18.5 14.5391 18.2893 14.9142 17.9142C15.2893 17.5391 15.5 17.0304 15.5 16.5V3.5H16.5V1.5H11.5V0.5H5.5ZM3.5 3.5H13.5V16.5H3.5V3.5ZM5.5 5.5V14.5H7.5V5.5H5.5ZM9.5 5.5V14.5H11.5V5.5H9.5Z"
                fill={color}
            />
        </svg>
    );
}

export function UserProfile({ color, onClick, scale }: IconProps) {
    const width = 24;
    const height = 25;

    const path =
        "M22.8274 20.3625C22.2379 18.9662 21.3824 17.6978 20.3087 16.6281C19.2381 15.5553 17.97 14.7 16.5743 14.1094C16.5618 14.1031 16.5493 14.1 16.5368 14.0938C18.4837 12.6875 19.7493 10.3969 19.7493 7.8125C19.7493 3.53125 16.2805 0.0625 11.9993 0.0625C7.71804 0.0625 4.24929 3.53125 4.24929 7.8125C4.24929 10.3969 5.51491 12.6875 7.46179 14.0969C7.44929 14.1031 7.43679 14.1062 7.42429 14.1125C6.02429 14.7031 4.76804 15.55 3.68991 16.6313C2.61713 17.7018 1.7618 18.9699 1.17116 20.3656C0.590916 21.732 0.277978 23.197 0.249285 24.6812C0.248451 24.7146 0.254301 24.7478 0.26649 24.7788C0.27868 24.8099 0.296963 24.8382 0.320261 24.8621C0.343559 24.886 0.371402 24.9049 0.402148 24.9179C0.432894 24.9308 0.465922 24.9375 0.499285 24.9375H2.37428C2.51179 24.9375 2.62116 24.8281 2.62428 24.6938C2.68678 22.2812 3.65553 20.0219 5.36803 18.3094C7.13991 16.5375 9.49304 15.5625 11.9993 15.5625C14.5055 15.5625 16.8587 16.5375 18.6305 18.3094C20.343 20.0219 21.3118 22.2812 21.3743 24.6938C21.3774 24.8313 21.4868 24.9375 21.6243 24.9375H23.4993C23.5326 24.9375 23.5657 24.9308 23.5964 24.9179C23.6272 24.9049 23.655 24.886 23.6783 24.8621C23.7016 24.8382 23.7199 24.8099 23.7321 24.7788C23.7443 24.7478 23.7501 24.7146 23.7493 24.6812C23.718 23.1875 23.4087 21.7344 22.8274 20.3625ZM11.9993 13.1875C10.5649 13.1875 9.21491 12.6281 8.19929 11.6125C7.18366 10.5969 6.62429 9.24687 6.62429 7.8125C6.62429 6.37813 7.18366 5.02812 8.19929 4.0125C9.21491 2.99687 10.5649 2.4375 11.9993 2.4375C13.4337 2.4375 14.7837 2.99687 15.7993 4.0125C16.8149 5.02812 17.3743 6.37813 17.3743 7.8125C17.3743 9.24687 16.8149 10.5969 15.7993 11.6125C14.7837 12.6281 13.4337 13.1875 11.9993 13.1875Z";

    return <Icon scale={scale} width={width} height={height} color={color} onClick={onClick} path={path} />;
}

export function DownCarrot() {
    return (
        <svg width="13" height="9" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M2.12875 0.790039L6.71875 5.38004L11.3088 0.790039L12.7188 2.21004L6.71875 8.21004L0.71875 2.21004L2.12875 0.790039Z"
                fill="#545454"
            />
        </svg>
    );
}

interface BackArrowProps {
    color?: string;
    rotate180Degrees?: boolean;
}

export function BackArrow({ color = "var(--audette-gray-600)", rotate180Degrees }: BackArrowProps) {
    return (
        <svg
            width="8"
            height="13"
            viewBox="0 0 8 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                transform: rotate180Degrees ? "rotate(180deg)" : undefined,
            }}
        >
            <path
                d="M7.91 10.7206L3.33 6.14062L7.91 1.55062L6.5 0.140625L0.5 6.14062L6.5 12.1406L7.91 10.7206Z"
                fill={color}
            />
        </svg>
    );
}

interface FilterIconProps {
    color?: string;
}

export function FilterIcon({ color }: FilterIconProps) {
    return <FilterListIcon style={{ color, fontSize: "1.5em" }} />;
}

interface OpenInNewProps {
    color?: string;
    fontSize?: string;
}

export function OpenInNew({ color = "var(--audette-gray-600)", fontSize = "1.25em" }: OpenInNewProps) {
    return <OpenInNewIcon style={{ color, fontSize }} />;
}

interface ImportExportProps {
    color?: string;
}

export function ImportIconOutlined({ color = "var(--audette-purple)" }: ImportExportProps) {
    return <FileUploadOutlinedIcon style={{ color }} />;
}

export function ImportIcon() {
    return <FileDownloadOutlinedIcon />;
}

interface AddProps {
    color?: string;
}

export function AddIcon({ color = "var(--audette-purple)" }: AddProps) {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M16.8789 9.50174H10.0218V16.3589H7.73605V9.50174H0.878906V7.21603H7.73605V0.358887H10.0218V7.21603H16.8789V9.50174Z"
                fill={color}
            />
        </svg>
    );
}

export function InfoIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10.997 8.19492L10.277 8.93092C9.70102 9.50692 9.34102 9.99492 9.34102 11.1949H7.74102V10.7949C7.74102 9.90692 8.10102 9.10692 8.67702 8.53092L9.66902 7.52292C9.96502 7.23492 10.141 6.83492 10.141 6.39492C10.141 5.50692 9.42102 4.79492 8.54102 4.79492C8.11667 4.79492 7.7097 4.96349 7.40965 5.26355C7.10959 5.56361 6.94102 5.97058 6.94102 6.39492H5.34102C5.34102 5.54623 5.67816 4.7323 6.27827 4.13218C6.87839 3.53206 7.69232 3.19492 8.54102 3.19492C9.38971 3.19492 10.2036 3.53206 10.8038 4.13218C11.4039 4.7323 11.741 5.54623 11.741 6.39492C11.741 7.09892 11.453 7.73092 10.997 8.19492ZM9.34102 14.3949H7.74102V12.7949H9.34102M8.54102 0.794922C7.49044 0.794922 6.45015 1.00185 5.47955 1.40389C4.50894 1.80592 3.62703 2.3952 2.88416 3.13807C1.38387 4.63836 0.541016 6.67319 0.541016 8.79492C0.541016 10.9167 1.38387 12.9515 2.88416 14.4518C3.62703 15.1946 4.50894 15.7839 5.47955 16.186C6.45015 16.588 7.49044 16.7949 8.54102 16.7949C10.6627 16.7949 12.6976 15.9521 14.1979 14.4518C15.6982 12.9515 16.541 10.9167 16.541 8.79492C16.541 4.37092 12.941 0.794922 8.54102 0.794922Z"
                fill="#AFAFAF"
            />
        </svg>
    );
}

interface ExpandProps {
    color?: string;
}

export function Expand({ color = "var(--audette-gray-600)" }: ExpandProps) {
    return <OpenInFullSharpIcon style={{ color, fontSize: "1.25em" }} />;
}

interface CollapseProps {
    color?: string;
}

export function Collapse({ color = "var(--audette-gray-600)" }: CollapseProps) {
    return <CloseFullscreenSharpIcon style={{ color, fontSize: "1.25em" }} />;
}

export function SadFace() {
    return (
        <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d={sadFacePath} fill="var(--audette-gray-400)" />
        </svg>
    );
}

const sadFacePath =
    "M28.8 16.834C28.8 13.4392 27.4514 10.1835 25.051 7.78302C22.6505 5.38255 19.3948 4.03398 16 4.03398C12.6052 4.03398 9.3495 5.38255 6.94903 7.78302C4.54857 10.1835 3.2 13.4392 3.2 16.834C3.2 20.2288 4.54857 23.4845 6.94903 25.885C9.3495 28.2854 12.6052 29.634 16 29.634C19.3948 29.634 22.6505 28.2854 25.051 25.885C27.4514 23.4845 28.8 20.2288 28.8 16.834ZM32 16.834C32 21.0774 30.3143 25.1471 27.3137 28.1477C24.3131 31.1483 20.2435 32.834 16 32.834C13.8988 32.834 11.8183 32.4201 9.87706 31.6161C7.93586 30.812 6.17203 29.6334 4.68629 28.1477C1.68571 25.1471 0 21.0774 0 16.834C0 12.5905 1.68571 8.52086 4.68629 5.52028C7.68687 2.51969 11.7565 0.833984 16 0.833984C18.1012 0.833984 20.1817 1.24784 22.1229 2.05191C24.0641 2.85599 25.828 4.03454 27.3137 5.52028C28.7994 7.00601 29.978 8.76984 30.7821 10.711C31.5861 12.6523 32 14.7328 32 16.834ZM21.6 10.434C22.88 10.434 24 11.554 24 12.834C24 14.114 22.88 15.234 21.6 15.234C20.32 15.234 19.2 14.114 19.2 12.834C19.2 11.554 20.32 10.434 21.6 10.434ZM12.8 12.834C12.8 14.114 11.68 15.234 10.4 15.234C9.12 15.234 8 14.114 8 12.834C8 11.554 9.12 10.434 10.4 10.434C11.68 10.434 12.8 11.554 12.8 12.834ZM16 20.034C18.8 20.034 21.264 21.186 22.704 22.93L20.432 25.202C19.712 24.034 18 23.234 16 23.234C14 23.234 12.288 24.034 11.568 25.202L9.296 22.93C10.736 21.186 13.2 20.034 16 20.034Z";

interface ArrowProps {
    color?: string;
}

const downArrowPath =
    "M0.457886 7.78511H3.89875V0.111989L5.62778 0.0861816V7.78511H9.06004L4.75896 12.0862L0.457886 7.78511Z";

export function DownArrow({ color = "#3B814B" }: ArrowProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 10 13" fill="none">
            <path d={downArrowPath} fill={color} />
        </svg>
    );
}

export function UpArrow({ color = "#CC303C" }: ArrowProps) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height="1em"
            viewBox="0 0 10 13"
            fill="none"
            style={{ transform: "rotate(180deg)" }}
        >
            <path d={downArrowPath} fill={color} />
        </svg>
    );
}

export function EmissionsIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M12.0001 4.08594C4.80012 5.68594 3.12012 10.6219 1.45612 14.7579L2.96812 15.2859L3.72812 13.4459C4.11212 13.5819 4.51212 13.6859 4.80012 13.6859C13.6001 13.6859 16.0001 0.0859375 16.0001 0.0859375C15.2001 1.68594 9.60012 1.88594 5.60012 2.68594C1.60012 3.48594 0.00012207 6.88594 0.00012207 8.48594C0.00012207 10.0859 1.40012 11.4859 1.40012 11.4859C4.00012 4.08594 12.0001 4.08594 12.0001 4.08594Z"
                fill="#3B814B"
            />
        </svg>
    );
}

export function ErrorIcon({ color = "#CC303C" }: { color?: string }) {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M9.74141 9.59492H8.14141V4.79492H9.74141M9.74141 12.7949H8.14141V11.1949H9.74141M8.94141 0.794922C7.89083 0.794922 6.85054 1.00185 5.87994 1.40389C4.90933 1.80592 4.02742 2.3952 3.28455 3.13807C1.78426 4.63836 0.941406 6.67319 0.941406 8.79492C0.941406 10.9167 1.78426 12.9515 3.28455 14.4518C4.02742 15.1946 4.90933 15.7839 5.87994 16.186C6.85054 16.588 7.89083 16.7949 8.94141 16.7949C11.0631 16.7949 13.098 15.9521 14.5983 14.4518C16.0986 12.9515 16.9414 10.9167 16.9414 8.79492C16.9414 7.74435 16.7345 6.70406 16.3324 5.73345C15.9304 4.76285 15.3411 3.88094 14.5983 3.13807C13.8554 2.3952 12.9735 1.80592 12.0029 1.40389C11.0323 1.00185 9.99198 0.794922 8.94141 0.794922Z"
                fill={color}
            />
        </svg>
    );
}

export function SuccessIcon({ color = "#28a745", style }: { color?: string; style?: CSSProperties }) {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
            <circle cx="8.5" cy="8.5" r="8" fill={color} />
        </svg>
    );
}

export function ChartIcon({ style }: { style?: CSSProperties }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none" style={style}>
            <path
                d="M5.6971 12.4444H3.91933V6.22222H5.6971V12.4444ZM9.25266 12.4444H7.47488V3.55556H9.25266V12.4444ZM12.8082 12.4444H11.0304V8.88889H12.8082V12.4444ZM14.586 14.2222H2.14155V1.77778H14.586V14.3111M14.586 0H2.14155C1.16377 0 0.36377 0.8 0.36377 1.77778V14.2222C0.36377 15.2 1.16377 16 2.14155 16H14.586C15.5638 16 16.3638 15.2 16.3638 14.2222V1.77778C16.3638 0.8 15.5638 0 14.586 0Z"
                fill="#545454"
            />
        </svg>
    );
}

export function TableIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path
                d="M2.20148 0.996582H14.6459C15.1174 0.996582 15.5696 1.18388 15.903 1.51728C16.2364 1.85068 16.4237 2.30286 16.4237 2.77436V13.441C16.4237 13.9125 16.2364 14.3647 15.903 14.6981C15.5696 15.0315 15.1174 15.2188 14.6459 15.2188H2.20148C1.72999 15.2188 1.2778 15.0315 0.944405 14.6981C0.611007 14.3647 0.423706 13.9125 0.423706 13.441V2.77436C0.423706 2.30286 0.611007 1.85068 0.944405 1.51728C1.2778 1.18388 1.72999 0.996582 2.20148 0.996582ZM2.20148 4.55214V8.10769H7.53482V4.55214H2.20148ZM9.3126 4.55214V8.10769H14.6459V4.55214H9.3126ZM2.20148 9.88547V13.441H7.53482V9.88547H2.20148ZM9.3126 9.88547V13.441H14.6459V9.88547H9.3126Z"
                fill="#545454"
            />
        </svg>
    );
}

export function MapIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M16.2665 0.0976562L16.1243 0.124323L11.3776 1.96432L6.04427 0.0976562L1.03094 1.78655C0.844271 1.84877 0.710938 2.00877 0.710938 2.21321V15.6532C0.710938 15.7711 0.757763 15.8841 0.841112 15.9675C0.924462 16.0508 1.03751 16.0977 1.15538 16.0977L1.2976 16.071L6.04427 14.231L11.3776 16.0977L16.3909 14.4088C16.5776 14.3465 16.7109 14.1865 16.7109 13.9821V0.542101C16.7109 0.424227 16.6641 0.311181 16.5808 0.227831C16.4974 0.144482 16.3844 0.0976562 16.2665 0.0976562ZM6.93316 2.29321L10.4887 3.53766V13.9021L6.93316 12.6577V2.29321ZM2.48872 3.17321L5.15538 2.27543V12.6754L2.48872 13.7065V3.17321ZM14.9332 13.0221L12.2665 13.9199V3.52877L14.9332 2.49766V13.0221Z"
                fill="#545454"
            />
        </svg>
    );
}

export function PinIcon() {
    return (
        <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6.19661 3.91797C6.74915 3.91797 7.27905 4.13746 7.66975 4.52816C8.06045 4.91886 8.27995 5.44877 8.27995 6.0013C8.27995 6.27489 8.22606 6.5458 8.12136 6.79856C8.01667 7.05132 7.86321 7.28099 7.66975 7.47444C7.4763 7.6679 7.24663 7.82135 6.99387 7.92605C6.74111 8.03075 6.4702 8.08464 6.19661 8.08464C5.64408 8.08464 5.11418 7.86514 4.72348 7.47444C4.33277 7.08374 4.11328 6.55384 4.11328 6.0013C4.11328 5.44877 4.33277 4.91886 4.72348 4.52816C5.11418 4.13746 5.64408 3.91797 6.19661 3.91797ZM6.19661 0.167969C7.74371 0.167969 9.22744 0.78255 10.3214 1.87651C11.4154 2.97047 12.0299 4.45421 12.0299 6.0013C12.0299 10.3763 6.19661 16.8346 6.19661 16.8346C6.19661 16.8346 0.363281 10.3763 0.363281 6.0013C0.363281 4.45421 0.977863 2.97047 2.07183 1.87651C3.16579 0.78255 4.64952 0.167969 6.19661 0.167969ZM6.19661 1.83464C5.09155 1.83464 4.03174 2.27362 3.25034 3.05502C2.46893 3.83643 2.02995 4.89623 2.02995 6.0013C2.02995 6.83464 2.02995 8.5013 6.19661 14.093C10.3633 8.5013 10.3633 6.83464 10.3633 6.0013C10.3633 4.89623 9.92429 3.83643 9.14289 3.05502C8.36149 2.27362 7.30168 1.83464 6.19661 1.83464Z"
                fill="#545454"
            />
        </svg>
    );
}

export function EditIcon({ color, width, height }: { color?: string; width?: string; height?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={width || "18"} height={height || "19"} viewBox="0 0 18 19">
            <path
                d="M11.0575 6.40039L11.9975 7.34039L2.9175 16.4004H1.9975V15.4804L11.0575 6.40039ZM14.6575 0.400391C14.4075 0.400391 14.1475 0.500391 13.9575 0.690391L12.1275 2.52039L15.8775 6.27039L17.7075 4.44039C18.0975 4.05039 18.0975 3.40039 17.7075 3.03039L15.3675 0.690391C15.1675 0.490391 14.9175 0.400391 14.6575 0.400391ZM11.0575 3.59039L-0.00250244 14.6504V18.4004H3.7475L14.8075 7.34039L11.0575 3.59039Z"
                fill={color || "#757575"}
            />
        </svg>
    );
}

export function OverviewIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
            <path
                d="M2 0.400391C0.89 0.400391 0 1.29039 0 2.40039V16.4004C0 17.5104 0.89 18.4004 2 18.4004H16C17.11 18.4004 18 17.5104 18 16.4004V2.40039C18 1.29039 17.11 0.400391 16 0.400391H2ZM2 2.40039H16V16.4004H2V2.40039ZM4 4.40039V6.40039H14V4.40039H4ZM4 8.40039V10.4004H14V8.40039H4ZM4 12.4004V14.4004H11V12.4004H4Z"
                fill="#545454"
            />
        </svg>
    );
}

export function FloorIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
            <path
                d="M10 5.40039V10.4004H9V5.40039H5V13.4004H9V12.4004H10V17.4004H9V14.4004H5V19.4004H12V17.4004H13V19.4004H19V17.4004H21V21.4004H3V3.40039H21V15.4004H19V10.4004H13V15.4004H12V9.40039H19V5.40039H10Z"
                fill="#545454"
            />
        </svg>
    );
}

export function RoofIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
            <path
                d="M19 16.1719H22L12 7.17188L2 16.1719H5L12 9.86187L19 16.1719ZM7 8.98188V7.17188H4V11.6719L7 8.98188Z"
                fill="#545454"
            />
        </svg>
    );
}
export function WindowIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
            <path
                d="M21 20.8301V2.83008H3V20.8301H1V23.8301H23V20.8301M19 4.83008V11.8301H13V4.83008M5 4.83008H11V11.8301H5M5 20.8301V13.8301H11V20.8301M13 20.8301V13.8301H19V20.8301H13Z"
                fill="#545454"
            />
        </svg>
    );
}

interface EnergyIconProps {
    size?: "medium" | "large";
}

export function EnergyIcon({ size = "large" }: EnergyIconProps) {
    return (
        <svg
            width={size === "medium" ? 20 : 24}
            height={size === "medium" ? 17 : 21}
            viewBox="0 0 20 17"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M3.76494 16.7949V9.26551H0.941406L10.3532 0.794922L19.7649 9.26551H16.9414V16.7949H3.76494ZM10.3532 3.32669L5.64729 7.56198V14.9126H15.0591V7.56198L10.3532 3.32669ZM9.88258 14.9126V11.1479H7.52964L10.8238 4.55963V8.32433H13.1767L9.88258 14.9126Z" />
        </svg>
    );
}

export function ConstructionIcon({ color = "var(--audette-black)" }: { color?: string }) {
    return (
        <svg width="22.2" height="21" xmlns="http://www.w3.org/2000/svg">
            <path
                fill={color}
                d="M18.1664 14.2913H15.9442V16.5136H18.1664M18.1664 9.8469H15.9442V12.0691H18.1664M20.3887 18.7358H11.4998V16.5136H13.722V14.2913H11.4998V12.0691H13.722V9.8469H11.4998V7.62467H20.3887M9.27756 5.40245H7.05534V3.18023H9.27756M9.27756 9.8469H7.05534V7.62467H9.27756M9.27756 14.2913H7.05534V12.0691H9.27756M9.27756 18.7358H7.05534V16.5136H9.27756M4.83312 5.40245H2.61089V3.18023H4.83312M4.83312 9.8469H2.61089V7.62467H4.83312M4.83312 14.2913H2.61089V12.0691H4.83312M4.83312 18.7358H2.61089V16.5136H4.83312M11.4998 5.40245V0.958008H0.388672V20.958H22.6109V5.40245H11.4998Z"
            />
        </svg>
    );
}

export function ElectricityIcon() {
    return (
        <svg width="9" height="17" viewBox="0 0 9 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M3.63636 6.61898V7.73171H6.37091L5.09091 10.299V9.18626H2.35636L3.63636 6.61898ZM5.09091 0.458984L0 10.6408H3.63636V16.459L8.72727 6.27717H5.09091V0.458984Z"
                fill="#545454"
            />
        </svg>
    );
}

export function NaturalGasIcon() {
    return (
        <svg width="13" height="17" viewBox="0 0 13 17" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M11.2645 8.24808C11.0601 7.98141 10.8112 7.75029 10.5801 7.51917C9.98451 6.98582 9.30894 6.60359 8.74003 6.04357C7.41555 4.74576 7.12221 2.60348 7.96668 0.958984C7.12221 1.16343 6.38441 1.62567 5.75328 2.13235C3.45099 3.98129 2.5443 7.24361 3.62878 10.0437C3.66433 10.1326 3.69989 10.2215 3.69989 10.337C3.69989 10.5326 3.56655 10.7104 3.38877 10.7815C3.18432 10.8704 2.97098 10.817 2.80209 10.6748C2.74875 10.6304 2.71319 10.5859 2.67764 10.5237C1.67316 9.25255 1.51316 7.43028 2.18873 5.97246C0.704248 7.18138 -0.104664 9.22589 0.0108952 11.1548C0.06423 11.5993 0.117565 12.0437 0.26868 12.4882C0.393128 13.0216 0.633135 13.5549 0.899809 14.026C1.85984 15.5638 3.52211 16.6661 5.30882 16.8883C7.2111 17.1283 9.24671 16.7817 10.7045 15.4661C12.3312 13.9905 12.9002 11.626 12.0646 9.59923L11.949 9.36811C11.7623 8.95921 11.2645 8.24808 11.2645 8.24808ZM8.45558 13.8482C8.20668 14.0616 7.79778 14.2927 7.47777 14.3816C6.48219 14.7372 5.48661 14.2394 4.89992 13.6527C5.95773 13.4038 6.58886 12.6215 6.77553 11.8304C6.92665 11.1193 6.6422 10.5326 6.52664 9.84813C6.41997 9.19033 6.43775 8.63031 6.67775 8.01696C6.84665 8.35475 7.02443 8.69254 7.23777 8.95921C7.92223 9.84813 8.99782 10.2392 9.22894 11.4482C9.26449 11.5726 9.28227 11.6971 9.28227 11.8304C9.30894 12.5593 8.98893 13.3593 8.45558 13.8482Z"
                fill="#545454"
            />
        </svg>
    );
}

export function AudetteIcon() {
    return (
        <svg width="17" height="11" viewBox="0 0 17 11" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.81034 0.985596H0.5V10.9166H3.81034V0.985596Z" />
            <path d="M11.5344 10.9166C14.2768 10.9166 16.4999 8.69349 16.4999 5.95111C16.4999 3.20873 14.2768 0.985596 11.5344 0.985596C8.79199 0.985596 6.56885 3.20873 6.56885 5.95111C6.56885 8.69349 8.79199 10.9166 11.5344 10.9166Z" />
        </svg>
    );
}

interface EquipmentIconProps {
    size?: "medium" | "large";
}

export function EquipmentIcon({ size = "large" }: EquipmentIconProps) {
    return (
        <svg
            width={size === "medium" ? 17 : 21}
            height={size === "medium" ? 17 : 21}
            viewBox="0 0 17 17"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M16.5336 15.4263L15.3977 16.5623C15.2478 16.7113 15.045 16.7949 14.8337 16.7949C14.6224 16.7949 14.4196 16.7113 14.2697 16.5623L4.7661 7.07467C4.50596 7.15004 4.23694 7.19039 3.96613 7.19467C3.45679 7.1943 2.9549 7.07236 2.50218 6.83897C2.04946 6.60559 1.659 6.2675 1.36325 5.85282C1.06749 5.43815 0.875006 4.95886 0.801783 4.45481C0.72856 3.95077 0.776717 3.43652 0.942252 2.95484L2.97417 4.98676L3.39815 4.56277L4.53411 3.42682L4.95809 3.00283L2.92617 0.970915C3.40786 0.80538 3.92211 0.757222 4.42615 0.830446C4.9302 0.903669 5.40948 1.09616 5.82416 1.39191C6.23884 1.68766 6.57692 2.07813 6.81031 2.53085C7.0437 2.98356 7.16564 3.48546 7.16601 3.99479C7.16173 4.2656 7.12137 4.53462 7.04601 4.79476L16.5336 14.2984C16.6826 14.4483 16.7663 14.651 16.7663 14.8624C16.7663 15.0737 16.6826 15.2765 16.5336 15.4263ZM0.99825 14.2984C0.849255 14.4483 0.765625 14.651 0.765625 14.8624C0.765625 15.0737 0.849255 15.2765 0.99825 15.4263L2.1342 16.5623C2.28409 16.7113 2.48684 16.7949 2.69818 16.7949C2.90952 16.7949 3.11228 16.7113 3.26216 16.5623L7.63799 12.1945L5.37408 9.93056M15.1657 0.794922L11.9658 2.39486V3.99479L10.2299 5.73073L11.8298 7.33066L13.5658 5.59473H15.1657L16.7656 2.39486L15.1657 0.794922Z" />
        </svg>
    );
}

interface WarningIconProps {
    width?: string;
    height?: string;
    style?: CSSProperties;
}

export function WarningIcon({ width = "20", height = "17", style }: WarningIconProps) {
    return (
        <svg
            className="warning-icon"
            width={width}
            height={height}
            viewBox="0 0 20 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={style}
        >
            <path
                d="M10.8709 10.4002H9.18668V6.18966H10.8709M10.8709 13.7686H9.18668V12.0844H10.8709M0.765625 16.2949H19.2919L10.0288 0.294922L0.765625 16.2949Z"
                fill="#CA8727"
            />
        </svg>
    );
}

export function WarningCountIcon({ style }: { style?: CSSProperties }) {
    return (
        <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
            <circle cx="10.3945" cy="10.8096" r="10" fill="#BB6E00" />
            <path
                d="M9.61253 13.0936L9.47253 6.00957H10.7465L10.6065 13.0936H9.61253ZM10.1445 15.8796C9.9112 15.8796 9.7152 15.8049 9.55653 15.6556C9.4072 15.4969 9.33253 15.3056 9.33253 15.0816C9.33253 14.8576 9.4072 14.6709 9.55653 14.5216C9.7152 14.3629 9.9112 14.2836 10.1445 14.2836C10.3685 14.2836 10.5552 14.3629 10.7045 14.5216C10.8632 14.6709 10.9425 14.8576 10.9425 15.0816C10.9425 15.3056 10.8632 15.4969 10.7045 15.6556C10.5552 15.8049 10.3685 15.8796 10.1445 15.8796Z"
                fill="white"
            />
        </svg>
    );
}

// equipment icons

export function HeatingIcon() {
    return (
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6.88675 0.000244141L5.03167 2.62826C5.65003 2.47367 6.26839 2.31908 6.88675 2.31908C7.5051 2.31908 8.12346 2.47367 8.74182 2.62826M13.8433 2.31908C12.9853 2.31908 12.2974 3.00701 12.2974 3.86498V9.0901C11.8027 9.53068 11.5244 10.1645 11.5244 10.8215C11.5244 12.1046 12.5602 13.1403 13.8433 13.1403C15.1264 13.1403 16.1621 12.1046 16.1621 10.8215C16.1621 10.1645 15.8838 9.53068 15.3892 9.09783V3.86498C15.3892 3.00701 14.7012 2.31908 13.8433 2.31908ZM13.8433 3.09203C14.2684 3.09203 14.6162 3.43986 14.6162 3.86498V4.63793H13.0703V3.86498C13.0703 3.43986 13.4181 3.09203 13.8433 3.09203ZM3.40849 3.63309L0.162109 3.86498L1.55341 6.80218C1.63071 6.18382 1.86259 5.56546 2.17177 5.0244C2.55824 4.48334 2.94472 4.01957 3.40849 3.63309ZM6.88675 3.86498C4.7225 3.86498 3.02201 5.56546 3.02201 7.72971C3.02201 9.89396 4.7225 11.5944 6.88675 11.5944C9.051 11.5944 10.7515 9.89396 10.7515 7.72971C10.7515 5.56546 9.051 3.86498 6.88675 3.86498ZM1.63071 8.65725L0.239404 11.5944L3.40849 11.9036C3.02201 11.5172 2.55824 10.9761 2.24907 10.435C1.86259 9.89396 1.708 9.27561 1.63071 8.65725ZM5.03167 12.8312L6.88675 15.4592L8.74182 12.8312C8.12346 12.9858 7.5051 13.1403 6.88675 13.1403C6.19109 13.1403 5.57274 12.9858 5.03167 12.8312Z"
                fill="#545454"
            />
        </svg>
    );
}

export function CoolingIcon() {
    return (
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10.4983 6.02543L7.8992 4.50054V2.80289L9.18371 1.51838L8.11704 0.459229L7.14803 1.42824L6.17901 0.459229L5.11234 1.51838L6.39685 2.80289V4.50054L4.51892 5.58223L3.01657 4.73341L2.58089 2.98317L1.13864 3.37378L1.49169 4.70336L0.162109 5.05641L0.55272 6.50618L2.30295 6.04045L3.8053 6.88928V9.06017L2.30295 9.90899L0.55272 9.44327L0.162109 10.893L1.49169 11.2461L1.13864 12.5681L2.5884 12.9588L3.05413 11.2085L4.55648 10.3597L6.39685 11.4414V13.139L5.11234 14.4235L6.17901 15.4827L7.14803 14.5137L8.11704 15.4827L9.17619 14.4235L7.8992 13.139V11.4414L10.4983 9.93153M5.27009 6.88928L7.14803 5.80007L9.02596 6.88928V9.05266L7.14803 10.1419L5.27009 9.05266M13.9086 2.71275C13.0748 2.71275 12.4062 3.38129 12.4062 4.2151V9.29303C11.9255 9.7212 11.6551 10.3372 11.6551 10.9757C11.6551 12.2226 12.6616 13.2292 13.9086 13.2292C15.1555 13.2292 16.1621 12.2226 16.1621 10.9757C16.1621 10.3372 15.8917 9.7212 15.4109 9.30054V4.2151C15.4109 3.38129 14.7424 2.71275 13.9086 2.71275ZM13.9086 3.46392C14.3217 3.46392 14.6598 3.80195 14.6598 4.2151V4.96627H13.1574V4.2151C13.1574 3.80195 13.4954 3.46392 13.9086 3.46392Z"
                fill="#545454"
            />
        </svg>
    );
}

export function AirHandlerIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M8.15013 7.67188C7.93827 7.67188 7.73509 7.75604 7.58529 7.90585C7.43548 8.05565 7.35133 8.25883 7.35133 8.47068C7.35133 8.68254 7.43548 8.88572 7.58529 9.03552C7.73509 9.18533 7.93827 9.26949 8.15013 9.26949C8.36198 9.26949 8.56516 9.18533 8.71496 9.03552C8.86477 8.88572 8.94893 8.68254 8.94893 8.47068C8.94893 8.25883 8.86477 8.05565 8.71496 7.90585C8.56516 7.75604 8.36198 7.67188 8.15013 7.67188ZM8.54953 0.482666C12.1441 0.482666 12.232 3.33439 10.3468 4.27697C9.55602 4.66839 9.20455 5.50713 9.05277 6.25002C9.4362 6.40978 9.7717 6.6574 10.0273 6.97692C12.9829 5.37932 16.1621 6.01037 16.1621 8.87008C16.1621 12.4647 13.3104 12.5446 12.3678 10.6514C11.9684 9.8606 11.1217 9.50913 10.3788 9.35735C10.219 9.74078 9.9714 10.0683 9.65187 10.3399C11.2415 13.2875 10.6104 16.4587 7.75073 16.4587C4.15612 16.4587 4.08423 13.599 5.9694 12.6564C6.75222 12.265 7.1037 11.4342 7.26346 10.6993C6.87204 10.5396 6.52856 10.284 6.27294 9.96444C3.32536 11.5461 0.162109 10.923 0.162109 8.07128C0.162109 4.47668 3.00584 4.38881 3.94843 6.28197C4.34783 7.07278 5.18657 7.41627 5.92946 7.56804C6.08123 7.18461 6.33685 6.84912 6.66436 6.5935C5.07474 3.64592 5.70579 0.482666 8.54953 0.482666Z"
                fill="#545454"
            />
        </svg>
    );
}

export function BoilerIcon() {
    return (
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6.88675 0.45874L5.03167 3.08676C5.65003 2.93217 6.26839 2.77758 6.88675 2.77758C7.5051 2.77758 8.12346 2.93217 8.74182 3.08676M13.8433 2.77758C12.9853 2.77758 12.2974 3.4655 12.2974 4.32347V9.5486C11.8027 9.98917 11.5244 10.623 11.5244 11.28C11.5244 12.5631 12.5602 13.5988 13.8433 13.5988C15.1264 13.5988 16.1621 12.5631 16.1621 11.28C16.1621 10.623 15.8838 9.98917 15.3892 9.55632V4.32347C15.3892 3.4655 14.7012 2.77758 13.8433 2.77758ZM13.8433 3.55053C14.2684 3.55053 14.6162 3.89835 14.6162 4.32347V5.09642H13.0703V4.32347C13.0703 3.89835 13.4181 3.55053 13.8433 3.55053ZM3.40849 4.09159L0.162109 4.32347L1.55341 7.26067C1.63071 6.64231 1.86259 6.02396 2.17177 5.48289C2.55824 4.94183 2.94472 4.47806 3.40849 4.09159ZM6.88675 4.32347C4.7225 4.32347 3.02201 6.02396 3.02201 8.18821C3.02201 10.3525 4.7225 12.0529 6.88675 12.0529C9.051 12.0529 10.7515 10.3525 10.7515 8.18821C10.7515 6.02396 9.051 4.32347 6.88675 4.32347ZM1.63071 9.11574L0.239404 12.0529L3.40849 12.3621C3.02201 11.9756 2.55824 11.4346 2.24907 10.8935C1.86259 10.3525 1.708 9.7341 1.63071 9.11574ZM5.03167 13.2897L6.88675 15.9177L8.74182 13.2897C8.12346 13.4442 7.5051 13.5988 6.88675 13.5988C6.19109 13.5988 5.57274 13.4442 5.03167 13.2897Z"
                fill="#545454"
            />
        </svg>
    );
}

export function ChillerIcon() {
    return (
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10.4983 6.48392L7.8992 4.95904V3.26139L9.18371 1.97688L8.11704 0.917725L7.14803 1.88674L6.17901 0.917725L5.11234 1.97688L6.39685 3.26139V4.95904L4.51892 6.04073L3.01657 5.1919L2.58089 3.44167L1.13864 3.83228L1.49169 5.16186L0.162109 5.51491L0.55272 6.96467L2.30295 6.49895L3.8053 7.34777V9.51866L2.30295 10.3675L0.55272 9.90176L0.162109 11.3515L1.49169 11.7046L1.13864 13.0266L2.5884 13.4173L3.05413 11.667L4.55648 10.8182L6.39685 11.8999V13.5975L5.11234 14.882L6.17901 15.9412L7.14803 14.9722L8.11704 15.9412L9.17619 14.882L7.8992 13.5975V11.8999L10.4983 10.39M5.27009 7.34777L7.14803 6.25857L9.02596 7.34777V9.51115L7.14803 10.6004L5.27009 9.51115M13.9086 3.17125C13.0748 3.17125 12.4062 3.83979 12.4062 4.67359V9.75153C11.9255 10.1797 11.6551 10.7957 11.6551 11.4342C11.6551 12.6811 12.6616 13.6877 13.9086 13.6877C15.1555 13.6877 16.1621 12.6811 16.1621 11.4342C16.1621 10.7957 15.8917 10.1797 15.4109 9.75904V4.67359C15.4109 3.83979 14.7424 3.17125 13.9086 3.17125ZM13.9086 3.92242C14.3217 3.92242 14.6598 4.26045 14.6598 4.67359V5.42477H13.1574V4.67359C13.1574 4.26045 13.4954 3.92242 13.9086 3.92242Z"
                fill="#545454"
            />
        </svg>
    );
}

export function WaterHeaterIcon() {
    return (
        <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M13.7621 2.3646C12.8741 2.3646 12.1621 3.0766 12.1621 3.9646V9.3726C11.6501 9.8286 11.3621 10.4846 11.3621 11.1646C11.3621 12.4926 12.4341 13.5646 13.7621 13.5646C15.0901 13.5646 16.1621 12.4926 16.1621 11.1646C16.1621 10.4846 15.8741 9.8286 15.3621 9.3806V3.9646C15.3621 3.0766 14.6501 2.3646 13.7621 2.3646ZM13.7621 3.1646C14.2021 3.1646 14.5621 3.5246 14.5621 3.9646V4.7646H12.9621V3.9646C12.9621 3.5246 13.3221 3.1646 13.7621 3.1646ZM4.96211 14.3646C2.31411 14.3646 0.162109 12.2126 0.162109 9.5646C0.162109 6.3646 4.96211 0.9646 4.96211 0.9646C4.96211 0.9646 9.76211 6.3646 9.76211 9.5646C9.76211 12.2126 7.61011 14.3646 4.96211 14.3646Z"
                fill="#545454"
            />
        </svg>
    );
}

export function DryerIcon() {
    return (
        <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1.76211 0.364624H11.3621C11.7865 0.364624 12.1934 0.533195 12.4935 0.833253C12.7935 1.13331 12.9621 1.54028 12.9621 1.96462V14.7646C12.9621 15.189 12.7935 15.5959 12.4935 15.896C12.1934 16.1961 11.7865 16.3646 11.3621 16.3646H1.76211C1.33776 16.3646 0.930797 16.1961 0.630738 15.896C0.33068 15.5959 0.162109 15.189 0.162109 14.7646V1.96462C0.162109 1.54028 0.33068 1.13331 0.630738 0.833253C0.930797 0.533195 1.33776 0.364624 1.76211 0.364624ZM2.56211 1.96462C2.34994 1.96462 2.14645 2.04891 1.99642 2.19894C1.8464 2.34897 1.76211 2.55245 1.76211 2.76462C1.76211 2.9768 1.8464 3.18028 1.99642 3.33031C2.14645 3.48034 2.34994 3.56462 2.56211 3.56462C2.77428 3.56462 2.97777 3.48034 3.12779 3.33031C3.27782 3.18028 3.36211 2.9768 3.36211 2.76462C3.36211 2.55245 3.27782 2.34897 3.12779 2.19894C2.97777 2.04891 2.77428 1.96462 2.56211 1.96462ZM4.96211 1.96462C4.74994 1.96462 4.54645 2.04891 4.39642 2.19894C4.2464 2.34897 4.16211 2.55245 4.16211 2.76462C4.16211 2.9768 4.2464 3.18028 4.39642 3.33031C4.54645 3.48034 4.74994 3.56462 4.96211 3.56462C5.17428 3.56462 5.37777 3.48034 5.52779 3.33031C5.67782 3.18028 5.76211 2.9768 5.76211 2.76462C5.76211 2.55245 5.67782 2.34897 5.52779 2.19894C5.37777 2.04891 5.17428 1.96462 4.96211 1.96462ZM6.56211 5.16462C5.28907 5.16462 4.06817 5.67034 3.168 6.57051C2.26782 7.47069 1.76211 8.69158 1.76211 9.96462C1.76211 11.2377 2.26782 12.4586 3.168 13.3587C4.06817 14.2589 5.28907 14.7646 6.56211 14.7646C7.83515 14.7646 9.05605 14.2589 9.95622 13.3587C10.8564 12.4586 11.3621 11.2377 11.3621 9.96462C11.3621 8.69158 10.8564 7.47069 9.95622 6.57051C9.05605 5.67034 7.83515 5.16462 6.56211 5.16462ZM3.45011 7.16462H4.96211C4.77011 8.26862 4.96211 8.90062 5.42611 9.39662C6.30611 10.2526 6.69011 11.3326 6.47411 12.7646H4.96211C5.15411 11.6606 4.96211 11.0286 4.49811 10.5326C3.61811 9.67662 3.24211 8.59662 3.45011 7.16462ZM6.65011 7.16462H8.16211C7.97011 8.26862 8.16211 8.90062 8.62611 9.39662C9.50611 10.2526 9.89011 11.3326 9.67411 12.7646H8.16211C8.35411 11.6606 8.16211 11.0286 7.69811 10.5326C6.81811 9.67662 6.44211 8.59662 6.65011 7.16462Z"
                fill="#545454"
            />
        </svg>
    );
}

export function WasherIcon() {
    return (
        <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M8.82611 7.70062C10.0741 8.94862 10.0741 10.9806 8.82611 12.2286C7.57811 13.4766 5.54611 13.4766 4.29811 12.2286L8.82611 7.70062ZM1.76211 0.364624H11.3621C11.7865 0.364624 12.1934 0.533195 12.4935 0.833253C12.7935 1.13331 12.9621 1.54028 12.9621 1.96462V14.7646C12.9621 15.189 12.7935 15.5959 12.4935 15.896C12.1934 16.1961 11.7865 16.3646 11.3621 16.3646H1.76211C1.33776 16.3646 0.930797 16.1961 0.630738 15.896C0.33068 15.5959 0.162109 15.189 0.162109 14.7646V1.96462C0.162109 1.54028 0.33068 1.13331 0.630738 0.833253C0.930797 0.533195 1.33776 0.364624 1.76211 0.364624ZM2.56211 1.96462C2.34994 1.96462 2.14645 2.04891 1.99642 2.19894C1.8464 2.34897 1.76211 2.55245 1.76211 2.76462C1.76211 2.9768 1.8464 3.18028 1.99642 3.33031C2.14645 3.48034 2.34994 3.56462 2.56211 3.56462C2.77428 3.56462 2.97777 3.48034 3.12779 3.33031C3.27782 3.18028 3.36211 2.9768 3.36211 2.76462C3.36211 2.55245 3.27782 2.34897 3.12779 2.19894C2.97777 2.04891 2.77428 1.96462 2.56211 1.96462ZM4.96211 1.96462C4.74994 1.96462 4.54645 2.04891 4.39642 2.19894C4.2464 2.34897 4.16211 2.55245 4.16211 2.76462C4.16211 2.9768 4.2464 3.18028 4.39642 3.33031C4.54645 3.48034 4.74994 3.56462 4.96211 3.56462C5.17428 3.56462 5.37777 3.48034 5.52779 3.33031C5.67782 3.18028 5.76211 2.9768 5.76211 2.76462C5.76211 2.55245 5.67782 2.34897 5.52779 2.19894C5.37777 2.04891 5.17428 1.96462 4.96211 1.96462ZM6.56211 5.16462C5.28907 5.16462 4.06817 5.67034 3.168 6.57051C2.26782 7.47069 1.76211 8.69158 1.76211 9.96462C1.76211 11.2377 2.26782 12.4586 3.168 13.3587C4.06817 14.2589 5.28907 14.7646 6.56211 14.7646C7.83515 14.7646 9.05605 14.2589 9.95622 13.3587C10.8564 12.4586 11.3621 11.2377 11.3621 9.96462C11.3621 8.69158 10.8564 7.47069 9.95622 6.57051C9.05605 5.67034 7.83515 5.16462 6.56211 5.16462Z"
                fill="#545454"
            />
        </svg>
    );
}

export function ElevatorIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M14.3843 2.1424V14.5868H1.93989V2.1424H14.3843ZM14.3843 0.364624H1.93989C0.962109 0.364624 0.162109 1.16462 0.162109 2.1424V14.5868C0.162109 15.5646 0.962109 16.3646 1.93989 16.3646H14.3843C15.3621 16.3646 16.1621 15.5646 16.1621 14.5868V2.1424C16.1621 1.16462 15.3621 0.364624 14.3843 0.364624ZM6.38433 13.698V10.1424H7.27322V7.92018C7.27322 6.9424 6.47322 6.1424 5.49544 6.1424H4.60655C3.62878 6.1424 2.82878 6.9424 2.82878 7.92018V10.1424H3.71766V13.698H6.38433ZM5.051 5.25351C5.66433 5.25351 6.16211 4.75574 6.16211 4.1424C6.16211 3.52907 5.66433 3.03129 5.051 3.03129C4.43766 3.03129 3.93989 3.52907 3.93989 4.1424C3.93989 4.75574 4.43766 5.25351 5.051 5.25351ZM13.4954 7.47574L11.2732 3.92018L9.051 7.47574H13.4954ZM9.051 9.25351L11.2732 12.8091L13.4954 9.25351H9.051Z"
                fill="#545454"
            />
        </svg>
    );
}

export function EscalatorIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M14.2222 0H1.77778C0.8 0 0 0.8 0 1.77778V14.2222C0 15.2 0.8 16 1.77778 16H14.2222C15.2 16 16 15.2 16 14.2222V1.77778C16 0.8 15.2 0 14.2222 0ZM12.4444 5.33333H10.9333L6.48889 13.3333H3.55556C2.81778 13.3333 2.22222 12.7378 2.22222 12C2.22222 11.2622 2.81778 10.6667 3.55556 10.6667H5.06667L9.51111 2.66667H12.4444C13.1822 2.66667 13.7778 3.26222 13.7778 4C13.7778 4.73778 13.1822 5.33333 12.4444 5.33333Z"
                fill="#545454"
            />
        </svg>
    );
}

export function SolarIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1.22611 11.5646H7.36211V9.16462H1.76211L1.22611 11.5646ZM8.96211 11.5646H15.0981L14.5621 9.16462H8.96211V11.5646ZM15.4501 13.1646H8.96211V16.3646H16.1621L15.4501 13.1646ZM0.162109 16.3646H7.36211V13.1646H0.874109L0.162109 16.3646ZM7.36211 5.16462H8.96211V7.56462H7.36211V5.16462ZM11.1701 4.53262L12.3061 3.39662L14.0021 5.09262L12.8741 6.22862L11.1701 4.53262ZM2.33011 5.09262L4.02611 3.39662L5.15411 4.53262L3.45811 6.22862L2.33011 5.09262ZM0.962109 0.364624H3.36211V1.96462H0.962109V0.364624ZM12.9621 0.364624H15.3621V1.96462H12.9621V0.364624ZM8.16211 4.36462C10.3701 4.36462 12.1621 2.57262 12.1621 0.364624H4.16211C4.16211 2.57262 5.95411 4.36462 8.16211 4.36462Z"
                fill="#545454"
            />
        </svg>
    );
}

export function Menu() {
    return (
        <svg width="16" height="5" viewBox="0 0 16 5" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12 2.5C12 1.96957 12.2107 1.46086 12.5858 1.08579C12.9609 0.710714 13.4696 0.5 14 0.5C14.5304 0.5 15.0391 0.710714 15.4142 1.08579C15.7893 1.46086 16 1.96957 16 2.5C16 3.03043 15.7893 3.53914 15.4142 3.91421C15.0391 4.28929 14.5304 4.5 14 4.5C13.4696 4.5 12.9609 4.28929 12.5858 3.91421C12.2107 3.53914 12 3.03043 12 2.5ZM6 2.5C6 1.96957 6.21071 1.46086 6.58579 1.08579C6.96086 0.710714 7.46957 0.5 8 0.5C8.53043 0.5 9.03914 0.710714 9.41421 1.08579C9.78929 1.46086 10 1.96957 10 2.5C10 3.03043 9.78929 3.53914 9.41421 3.91421C9.03914 4.28929 8.53043 4.5 8 4.5C7.46957 4.5 6.96086 4.28929 6.58579 3.91421C6.21071 3.53914 6 3.03043 6 2.5ZM0 2.5C0 1.96957 0.210714 1.46086 0.585786 1.08579C0.960859 0.710714 1.46957 0.5 2 0.5C2.53043 0.5 3.03914 0.710714 3.41421 1.08579C3.78929 1.46086 4 1.96957 4 2.5C4 3.03043 3.78929 3.53914 3.41421 3.91421C3.03914 4.28929 2.53043 4.5 2 4.5C1.46957 4.5 0.960859 4.28929 0.585786 3.91421C0.210714 3.53914 0 3.03043 0 2.5Z"
                fill="#545454"
            />
        </svg>
    );
}

export function Check({ height = 17, width = 17, style }: { height?: number; width?: number; style?: CSSProperties }) {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 17 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={style}
        >
            <path
                d="M16.9434 2.15877L5.97193 13.1302L0.943359 8.10163L2.2325 6.81249L5.97193 10.5428L15.6542 0.869629L16.9434 2.15877Z"
                fill="#3B814B"
            />
        </svg>
    );
}

export function ExportIcon() {
    return (
        <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6.93967 0.5L3.65929 3.78038H6.11958V11.1612H7.75977V3.78038H10.2201M11.8602 16.5H2.0191C1.10879 16.5 0.378906 15.7619 0.378906 14.8598V7.06077C0.378906 6.62576 0.551712 6.20857 0.859307 5.90097C1.1669 5.59338 1.58409 5.42057 2.0191 5.42057H4.47938V7.06077H2.0191V14.8598H11.8602V7.06077H9.39996V5.42057H11.8602C12.2953 5.42057 12.7124 5.59338 13.02 5.90097C13.3276 6.20857 13.5004 6.62576 13.5004 7.06077V14.8598C13.5004 15.2948 13.3276 15.712 13.02 16.0196C12.7124 16.3272 12.2953 16.5 11.8602 16.5Z"
                fill="#545454"
            />
        </svg>
    );
}

export function LeafIcon() {
    return (
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12.5 4.54062C5.3 6.14062 3.62 11.0766 1.956 15.2126L3.468 15.7406L4.228 13.9006C4.612 14.0366 5.012 14.1406 5.3 14.1406C14.1 14.1406 16.5 0.540619 16.5 0.540619C15.7 2.14062 10.1 2.34062 6.1 3.14062C2.1 3.94062 0.5 7.34062 0.5 8.94062C0.5 10.5406 1.9 11.9406 1.9 11.9406C4.5 4.54062 12.5 4.54062 12.5 4.54062Z"
                fill="#3B814B"
            />
        </svg>
    );
}

export function HandHoldingMoneyIcon() {
    return (
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M11.9286 0.331116C9.82571 0.331116 8.11905 2.03778 8.11905 4.14064C8.11905 6.2435 9.82571 7.95016 11.9286 7.95016C14.0314 7.95016 15.7381 6.2435 15.7381 4.14064C15.7381 2.03778 14.0314 0.331116 11.9286 0.331116ZM11.9286 6.42635C10.6638 6.42635 9.64286 5.4054 9.64286 4.14064C9.64286 2.87588 10.6638 1.85493 11.9286 1.85493C13.1933 1.85493 14.2143 2.87588 14.2143 4.14064C14.2143 5.4054 13.1933 6.42635 11.9286 6.42635ZM14.2143 10.9978H12.6905C12.6905 10.0835 12.119 9.26064 11.2657 8.94064L6.57238 7.18826H0.5V15.5692H5.07143V14.4721L10.4048 15.9502L16.5 14.0454V13.2835C16.5 12.0187 15.479 10.9978 14.2143 10.9978ZM3.54762 14.0454H2.02381V8.71207H3.54762V14.0454ZM10.3819 14.3578L5.07143 12.9025V8.71207H6.2981L10.7324 10.3654C10.9914 10.4644 11.1667 10.7159 11.1667 10.9978C11.1667 10.9978 9.64286 10.9597 9.41429 10.8835L7.60095 10.2816L7.12095 11.7292L8.93429 12.3311C9.32286 12.4606 9.72667 12.5216 10.1381 12.5216H14.2143C14.5114 12.5216 14.7781 12.7044 14.9 12.9559L10.3819 14.3578Z"
                fill="black"
            />
        </svg>
    );
}

export function NewPropertyIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M7.93936 3.92848V0.880859H0.320312V14.5951H8.96793C8.79014 14.1068 8.69986 13.591 8.70127 13.0713H7.93936V11.5475H8.96793C9.0832 11.1924 9.25007 10.8561 9.46317 10.5494V10.0237H7.93936V8.49991H9.46317V6.9761H7.93936V5.45229H14.0346V8.56848C14.573 8.65201 15.0903 8.83824 15.5584 9.11705V3.92848M3.36793 13.0713H1.84412V11.5475H3.36793M3.36793 10.0237H1.84412V8.49991H3.36793M3.36793 6.9761H1.84412V5.45229H3.36793M3.36793 3.92848H1.84412V2.40467H3.36793M6.41555 13.0713H4.89174V11.5475H6.41555M6.41555 10.0237H4.89174V8.49991H6.41555M6.41555 6.9761H4.89174V5.45229H6.41555M6.41555 3.92848H4.89174V2.40467H6.41555M10.987 8.49991H12.5108V6.9761H10.987M10.987 6.9761V8.49991H12.5108V6.9761M10.987 6.9761V8.49991H12.5108V6.9761M14.0346 10.0237V12.3094H16.3203V13.8332H14.0346V16.119H12.5108V13.8332H10.2251V12.3094H12.5108V10.0237H14.0346Z"
                fill="#545454"
            />
        </svg>
    );
}

export function NewFundIcon() {
    return (
        <svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M11.5751 10.4048H13.0989V12.6905H15.3846V14.2143H13.0989V16.5H11.5751V14.2143H9.28934V12.6905H11.5751V10.4048ZM7.38458 0.5L14.6227 4.30952V5.83333H0.146484V4.30952L7.38458 0.5ZM10.8132 7.35714H13.0989V8.94191L12.337 8.88095C11.8036 8.88095 11.2932 8.97238 10.8132 9.14V7.35714ZM0.146484 16.5V14.2143H7.82648C7.97125 15.0829 8.36744 15.8676 8.93125 16.5H0.146484ZM6.24172 7.35714H8.52744V10.9229C8.17696 11.4486 7.93315 12.0429 7.82648 12.6905H6.24172V7.35714ZM1.67029 7.35714H3.95601V12.6905H1.67029V7.35714Z"
                fill="#545454"
            />
        </svg>
    );
}

export function FundIcon() {
    return (
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M7.95685 0.5L0.71875 4.30952V5.83333H15.1949V4.30952M11.3854 7.35714V12.6905H13.6711V7.35714M0.71875 16.5H15.1949V14.2143H0.71875M6.81399 7.35714V12.6905H9.0997V7.35714M2.24256 7.35714V12.6905H4.52827V7.35714H2.24256Z"
                fill="#545454"
            />
        </svg>
    );
}

export function EnergyTypeIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6.94216 0.294922C6.64219 0.294922 6.34223 0.408908 6.09626 0.648878L1.29686 5.44828C0.822921 5.91622 0.822921 6.67212 1.29686 7.14007L6.09626 11.9395C6.5642 12.4134 7.32011 12.4134 7.78805 11.9395L12.5875 7.14007C13.0614 6.67212 13.0614 5.91622 12.5875 5.44828L7.78805 0.648878C7.54208 0.408908 7.24212 0.294922 6.94216 0.294922Z"
                fill="#D5BBF9"
            />
        </svg>
    );
}

export function BuildingIcon() {
    return (
        <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12.0153 2.10518V15.1961H7.65163V12.6506H6.19709V15.1961H1.83345V2.10518H12.0153ZM9.10618 5.01427H10.5607V3.55973H9.10618V5.01427ZM6.19709 5.01427H7.65163V3.55973H6.19709V5.01427ZM3.288 5.01427H4.74254V3.55973H3.288V5.01427ZM9.10618 7.92336H10.5607V6.46882H9.10618V7.92336ZM6.19709 7.92336H7.65163V6.46882H6.19709V7.92336ZM3.288 7.92336H4.74254V6.46882H3.288V7.92336ZM9.10618 10.8325H10.5607V9.37791H9.10618V10.8325ZM6.19709 10.8325H7.65163V9.37791H6.19709V10.8325ZM3.288 10.8325H4.74254V9.37791H3.288V10.8325ZM9.10618 13.7415H10.5607V12.287H9.10618V13.7415ZM3.288 13.7415H4.74254V12.287H3.288V13.7415ZM13.4698 0.650635H0.378906V16.6506H13.4698V0.650635Z"
                fill="black"
            />
        </svg>
    );
}

interface ResetIconProps {
    height?: number;
    color?: string;
}

export function ResetIcon({ height = 16, color = "#545454" }: ResetIconProps) {
    return (
        <svg width="16" height={height} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M13.65 2.35C12.2 0.9 10.21 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16C11.73 16 14.84 13.45 15.73 10H13.65C12.83 12.33 10.61 14 8 14C6.4087 14 4.88258 13.3679 3.75736 12.2426C2.63214 11.1174 2 9.5913 2 8C2 6.4087 2.63214 4.88258 3.75736 3.75736C4.88258 2.63214 6.4087 2 8 2C9.66 2 11.14 2.69 12.22 3.78L9 7H16V0L13.65 2.35Z"
                fill={color}
            />
        </svg>
    );
}
interface DownChevronIconProps {
    color?: string;
}
export function DownChevronIcon({ color = "white" }: DownChevronIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="10" viewBox="0 0 17 10" fill="none">
            <path
                d="M2.63977 0.0546875L8.75977 6.17469L14.8798 0.0546875L16.7598 1.94802L8.75977 9.94802L0.759766 1.94802L2.63977 0.0546875Z"
                fill={color}
            />
        </svg>
    );
}
export function RightChevronIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
            <path
                d="M0 14.1076L6.10667 8.00098L0 1.88098L1.88 0.000976562L9.88 8.00098L1.88 16.001L0 14.1076Z"
                fill="black"
            />
        </svg>
    );
}

interface CogWheelIconProps {
    color?: string;
}

export function CogWheelIcon({ color = "white" }: CogWheelIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path
                d="M8.49831 10.801C7.7557 10.801 7.04351 10.506 6.51841 9.98088C5.99331 9.45577 5.69831 8.74358 5.69831 8.00098C5.69831 7.25837 5.99331 6.54618 6.51841 6.02108C7.04351 5.49598 7.7557 5.20098 8.49831 5.20098C9.24092 5.20098 9.95311 5.49598 10.4782 6.02108C11.0033 6.54618 11.2983 7.25837 11.2983 8.00098C11.2983 8.74358 11.0033 9.45577 10.4782 9.98088C9.95311 10.506 9.24092 10.801 8.49831 10.801ZM14.4423 8.77698C14.4743 8.52098 14.4983 8.26498 14.4983 8.00098C14.4983 7.73698 14.4743 7.47298 14.4423 7.20098L16.1303 5.89698C16.2823 5.77698 16.3223 5.56098 16.2263 5.38498L14.6263 2.61698C14.5303 2.44098 14.3143 2.36898 14.1383 2.44098L12.1463 3.24098C11.7303 2.92898 11.2983 2.65698 10.7943 2.45698L10.4983 0.336977C10.4663 0.144977 10.2983 0.000976562 10.0983 0.000976562H6.89831C6.69831 0.000976562 6.53031 0.144977 6.49831 0.336977L6.20231 2.45698C5.69831 2.65698 5.26631 2.92898 4.85031 3.24098L2.85831 2.44098C2.68231 2.36898 2.46631 2.44098 2.37031 2.61698L0.770309 5.38498C0.666309 5.56098 0.714309 5.77698 0.866309 5.89698L2.55431 7.20098C2.52231 7.47298 2.49831 7.73698 2.49831 8.00098C2.49831 8.26498 2.52231 8.52098 2.55431 8.77698L0.866309 10.105C0.714309 10.225 0.666309 10.441 0.770309 10.617L2.37031 13.385C2.46631 13.561 2.68231 13.625 2.85831 13.561L4.85031 12.753C5.26631 13.073 5.69831 13.345 6.20231 13.545L6.49831 15.665C6.53031 15.857 6.69831 16.001 6.89831 16.001H10.0983C10.2983 16.001 10.4663 15.857 10.4983 15.665L10.7943 13.545C11.2983 13.337 11.7303 13.073 12.1463 12.753L14.1383 13.561C14.3143 13.625 14.5303 13.561 14.6263 13.385L16.2263 10.617C16.3223 10.441 16.2823 10.225 16.1303 10.105L14.4423 8.77698Z"
                fill={color}
            />
        </svg>
    );
}

interface HelpIconProps {
    color?: string;
}

export function HelpIcon({ color = "white" }: HelpIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path
                d="M11.2177 7.40098L10.4977 8.13698C9.92172 8.71298 9.56172 9.20098 9.56172 10.401H7.96172V10.001C7.96172 9.11298 8.32172 8.31298 8.89772 7.73698L9.88972 6.72898C10.1857 6.44098 10.3617 6.04098 10.3617 5.60098C10.3617 4.71298 9.64172 4.00098 8.76172 4.00098C8.33737 4.00098 7.93041 4.16955 7.63035 4.46961C7.33029 4.76966 7.16172 5.17663 7.16172 5.60098H5.56172C5.56172 4.75228 5.89886 3.93835 6.49898 3.33823C7.09909 2.73812 7.91303 2.40098 8.76172 2.40098C9.61041 2.40098 10.4243 2.73812 11.0245 3.33823C11.6246 3.93835 11.9617 4.75228 11.9617 5.60098C11.9617 6.30498 11.6737 6.93698 11.2177 7.40098ZM9.56172 13.601H7.96172V12.001H9.56172M8.76172 0.000976562C7.71114 0.000976562 6.67086 0.207903 5.70025 0.60994C4.72965 1.01198 3.84773 1.60125 3.10486 2.34412C1.60457 3.84441 0.761719 5.87924 0.761719 8.00098C0.761719 10.1227 1.60457 12.1575 3.10486 13.6578C3.84773 14.4007 4.72965 14.99 5.70025 15.392C6.67086 15.7941 7.71114 16.001 8.76172 16.001C10.8835 16.001 12.9183 15.1581 14.4186 13.6578C15.9189 12.1575 16.7617 10.1227 16.7617 8.00098C16.7617 3.57698 13.1617 0.000976562 8.76172 0.000976562Z"
                fill={color}
            />
        </svg>
    );
}

export function LogOutIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
            <path
                d="M12.7617 3.20098L11.6337 4.32898L13.6977 6.40098H5.56172V8.00098H13.6977L11.6337 10.065L12.7617 11.201L16.7617 7.20098M2.36172 1.60098H8.76172V0.000976562H2.36172C1.48172 0.000976562 0.761719 0.720976 0.761719 1.60098V12.801C0.761719 13.681 1.48172 14.401 2.36172 14.401H8.76172V12.801H2.36172V1.60098Z"
                fill="black"
            />
        </svg>
    );
}

interface PlusIconProps {
    height?: number;
}

export function PlusIcon({ height = 16 }: PlusIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height={height} viewBox="0 0 17 16" fill="none">
            <path
                d="M16.7617 9.14383H9.90458V16.001H7.61886V9.14383H0.761719V6.85812H7.61886V0.000976562H9.90458V6.85812H16.7617V9.14383Z"
                fill="black"
            />
        </svg>
    );
}

export function ReorderIcon({ height = 16 }: PlusIconProps) {
    return (
        <svg height={height} viewBox="0 0 17 12" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 1 L3 9 L1 9 L4 11 L7 9 L5 9 L5 1 Z" fill="currentColor" />
            <path d="M14 11 L14 3 L16 3 L13 1 L10 3 L12 3 L12 11 Z" fill="currentColor" />
        </svg>
    );
}

interface CheckMarkIconProps {
    height?: number;
}

export function CheckMarkIcon({ height = 16 }: CheckMarkIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height={height} viewBox="0 0 17 16" fill="none">
            <path d="M4 11.5L7 15L13 4" stroke="black" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export function MoreOptionsIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="4" viewBox="0 0 16 4" fill="none">
            <path
                d="M12 2.00098C12 1.47054 12.2107 0.961836 12.5858 0.586763C12.9609 0.211691 13.4696 0.000976563 14 0.000976563C14.5304 0.000976563 15.0391 0.211691 15.4142 0.586763C15.7893 0.961836 16 1.47054 16 2.00098C16 2.53141 15.7893 3.04012 15.4142 3.41519C15.0391 3.79026 14.5304 4.00098 14 4.00098C13.4696 4.00098 12.9609 3.79026 12.5858 3.41519C12.2107 3.04012 12 2.53141 12 2.00098ZM6 2.00098C6 1.47054 6.21071 0.961836 6.58579 0.586763C6.96086 0.211691 7.46957 0.000976563 8 0.000976563C8.53043 0.000976563 9.03914 0.211691 9.41421 0.586763C9.78929 0.961836 10 1.47054 10 2.00098C10 2.53141 9.78929 3.04012 9.41421 3.41519C9.03914 3.79026 8.53043 4.00098 8 4.00098C7.46957 4.00098 6.96086 3.79026 6.58579 3.41519C6.21071 3.04012 6 2.53141 6 2.00098ZM0 2.00098C0 1.47054 0.210714 0.961836 0.585786 0.586763C0.960859 0.211691 1.46957 0.000976562 2 0.000976562C2.53043 0.000976562 3.03914 0.211691 3.41421 0.586763C3.78929 0.961836 4 1.47054 4 2.00098C4 2.53141 3.78929 3.04012 3.41421 3.41519C3.03914 3.79026 2.53043 4.00098 2 4.00098C1.46957 4.00098 0.960859 3.79026 0.585786 3.41519C0.210714 3.04012 0 2.53141 0 2.00098Z"
                fill="black"
            />
        </svg>
    );
}

export function QuestionMarkIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="#AFAFAF">
            <path
                d="M11.2177 7.40098L10.4977 8.13698C9.92172 8.71298 9.56172 9.20098 9.56172 10.401H7.96172V10.001C7.96172 9.11298 8.32172 8.31298 8.89772 7.73698L9.88972 6.72898C10.1857 6.44098 10.3617 6.04098 10.3617 5.60098C10.3617 4.71298 9.64172 4.00098 8.76172 4.00098C8.33737 4.00098 7.93041 4.16955 7.63035 4.46961C7.33029 4.76966 7.16172 5.17663 7.16172 5.60098H5.56172C5.56172 4.75228 5.89886 3.93835 6.49898 3.33823C7.09909 2.73812 7.91303 2.40098 8.76172 2.40098C9.61041 2.40098 10.4243 2.73812 11.0245 3.33823C11.6246 3.93835 11.9617 4.75228 11.9617 5.60098C11.9617 6.30498 11.6737 6.93698 11.2177 7.40098ZM9.56172 13.601H7.96172V12.001H9.56172M8.76172 0.000976562C7.71114 0.000976562 6.67086 0.207903 5.70025 0.60994C4.72965 1.01198 3.84773 1.60125 3.10486 2.34412C1.60457 3.84441 0.761719 5.87924 0.761719 8.00098C0.761719 10.1227 1.60457 12.1575 3.10486 13.6578C3.84773 14.4007 4.72965 14.99 5.70025 15.392C6.67086 15.7941 7.71114 16.001 8.76172 16.001C10.8835 16.001 12.9183 15.1581 14.4186 13.6578C15.9189 12.1575 16.7617 10.1227 16.7617 8.00098C16.7617 3.57698 13.1617 0.000976562 8.76172 0.000976562Z"
                fill="#AFAFAF"
            />
        </svg>
    );
}

export function CalendarIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <path
                d="M3.89922 8.5H5.49922V10.1H3.89922V8.5ZM15.0992 3.7V14.9C15.0992 15.788 14.3872 16.5 13.4992 16.5H2.29922C1.41122 16.5 0.699219 15.78 0.699219 14.9V3.7C0.699219 2.82 1.41922 2.1 2.29922 2.1H3.09922V0.5H4.69922V2.1H11.0992V0.5H12.6992V2.1H13.4992C14.3872 2.1 15.0992 2.82 15.0992 3.7ZM2.29922 5.3H13.4992V3.7H2.29922V5.3ZM13.4992 14.9V6.9H2.29922V14.9H13.4992ZM10.2992 10.1H11.8992V8.5H10.2992V10.1ZM7.09922 10.1H8.69922V8.5H7.09922V10.1Z"
                fill="black"
            />
        </svg>
    );
}

export function DollarIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="17" viewBox="0 0 10 17" fill="none">
            <path
                d="M0.900391 11.1667H2.67817C2.67817 12.1267 3.89595 12.9444 5.34484 12.9444C6.79372 12.9444 8.0115 12.1267 8.0115 11.1667C8.0115 10.1889 7.08706 9.83333 5.1315 9.36222C3.24706 8.89111 0.900391 8.30444 0.900391 5.83333C0.900391 4.24222 2.20706 2.89111 4.0115 2.43778V0.5H6.67817V2.43778C8.48261 2.89111 9.78928 4.24222 9.78928 5.83333H8.0115C8.0115 4.87333 6.79372 4.05556 5.34484 4.05556C3.89595 4.05556 2.67817 4.87333 2.67817 5.83333C2.67817 6.81111 3.60261 7.16667 5.55817 7.63778C7.44261 8.10889 9.78928 8.69556 9.78928 11.1667C9.78928 12.7578 8.48261 14.1089 6.67817 14.5622V16.5H4.0115V14.5622C2.20706 14.1089 0.900391 12.7578 0.900391 11.1667Z"
                fill="black"
            />
        </svg>
    );
}

export function UserIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
            <path
                d="M13.6634 11.0553H10.1079V12.8331H13.6634V14.6109L16.3301 11.9442L13.6634 9.27756V11.0553ZM7.44119 0.388672C5.48563 0.388672 3.88563 1.98867 3.88563 3.94423C3.88563 5.89978 5.48563 7.49978 7.44119 7.49978C9.39674 7.49978 10.9967 5.89978 10.9967 3.94423C10.9967 1.98867 9.39674 0.388672 7.44119 0.388672ZM7.44119 2.16645C8.41897 2.16645 9.21897 2.96645 9.21897 3.94423C9.21897 4.92201 8.41897 5.72201 7.44119 5.72201C6.46341 5.72201 5.66341 4.92201 5.66341 3.94423C5.66341 2.96645 6.46341 2.16645 7.44119 2.16645ZM7.44119 8.38867C5.04119 8.38867 0.330078 9.54423 0.330078 11.9442V14.6109H8.77452C8.50786 14.0776 8.41897 13.5442 8.33008 12.922H2.01897V11.9442C2.01897 11.4109 4.77452 10.0776 7.44119 10.0776C7.88563 10.0776 8.33008 10.1664 8.77452 10.1664C9.04119 9.63312 9.30786 9.09978 9.7523 8.65534C8.86341 8.47756 8.06341 8.38867 7.44119 8.38867Z"
                fill="black"
            />
        </svg>
    );
}

export function ReinviteIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
            <path
                d="M1.78462 0.59082C0.984624 0.59082 0.330078 1.24537 0.330078 2.04537V10.7726C0.330078 11.5726 0.984624 12.2272 1.78462 12.2272H9.42099C9.22317 11.7648 9.10041 11.2737 9.05735 10.7726H1.78462V3.49991L7.60281 7.13628L13.421 3.49991V5.68173C13.5422 5.67707 13.6635 5.67707 13.7846 5.68173C14.1521 5.68282 14.5182 5.72675 14.8755 5.81264V2.04537C14.8755 1.24537 14.221 0.59082 13.421 0.59082H1.78462ZM1.78462 2.04537H13.421L7.60281 5.68173L1.78462 2.04537ZM13.421 6.409L11.7846 8.04537L13.421 9.68173V8.59082C14.4246 8.59082 15.2392 9.40537 15.2392 10.409C15.2392 10.6999 15.1737 10.9763 15.0501 11.2235L15.8428 12.0163C16.1483 11.5581 16.3301 11.0054 16.3301 10.409C16.3301 8.80173 15.0283 7.49991 13.421 7.49991V6.409ZM10.9992 8.80173C10.6937 9.25991 10.5119 9.81264 10.5119 10.409C10.5119 12.0163 11.8137 13.3181 13.421 13.3181V14.409L15.0574 12.7726L13.421 11.1363V12.2272C12.4174 12.2272 11.6028 11.4126 11.6028 10.409C11.6028 10.1181 11.6683 9.84173 11.7919 9.59446L10.9992 8.80173Z"
                fill="black"
            />
        </svg>
    );
}

export function CentralPlantIcon() {
    return (
        <svg
            style={{ marginRight: "10px" }}
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="16"
            viewBox="0 0 11 16"
            fill="none"
        >
            <path
                d="M2.25846 0C1.33346 0 0.591797 0.712 0.591797 1.6V11.2C0.591797 12.088 1.33346 12.8 2.25846 12.8H3.0918V14.4H0.591797V16H3.0918C4.0168 16 4.75846 15.288 4.75846 14.4V12.8H6.42513V14.4C6.42513 15.288 7.1668 16 8.0918 16H10.5918V14.4H8.0918V12.8H8.92513C9.85013 12.8 10.5918 12.088 10.5918 11.2V1.6C10.5918 0.712 9.85013 0 8.92513 0H2.25846ZM5.5918 2.376C6.03382 2.376 6.45775 2.54457 6.77031 2.84463C7.08287 3.14469 7.25846 3.55165 7.25846 3.976C7.25846 4.18611 7.21535 4.39417 7.1316 4.58829C7.04784 4.78241 6.92507 4.9588 6.77031 5.10737C6.61554 5.25594 6.43181 5.3738 6.2296 5.45421C6.02739 5.53462 5.81067 5.576 5.5918 5.576C5.14977 5.576 4.72585 5.40743 4.41329 5.10737C4.10073 4.80731 3.92513 4.40035 3.92513 3.976C3.92513 3.55165 4.10073 3.14469 4.41329 2.84463C4.72585 2.54457 5.14977 2.376 5.5918 2.376ZM3.92513 10H7.25846V11.2H3.92513V10Z"
                fill="black"
            />
        </svg>
    );
}

export function UnitCostIcon() {
    return (
        <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M0.5 10.8073H2.27778C2.27778 11.7673 3.49556 12.5851 4.94444 12.5851C6.39333 12.5851 7.61111 11.7673 7.61111 10.8073C7.61111 9.82951 6.68667 9.47396 4.73111 9.00285C2.84667 8.53174 0.5 7.94507 0.5 5.47396C0.5 3.88285 1.80667 2.53174 3.61111 2.0784V0.140625H6.27778V2.0784C8.08222 2.53174 9.38889 3.88285 9.38889 5.47396H7.61111C7.61111 4.51396 6.39333 3.69618 4.94444 3.69618C3.49556 3.69618 2.27778 4.51396 2.27778 5.47396C2.27778 6.45174 3.20222 6.80729 5.15778 7.2784C7.04222 7.74951 9.38889 8.33618 9.38889 10.8073C9.38889 12.3984 8.08222 13.7495 6.27778 14.2028V16.1406H3.61111V14.2028C1.80667 13.7495 0.5 12.3984 0.5 10.8073Z"
                fill="#545454"
            />
        </svg>
    );
}

export function SizingIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M16.268 14.772L15.1321 15.908C14.9822 16.057 14.7794 16.1406 14.5681 16.1406C14.3567 16.1406 14.154 16.057 14.0041 15.908L4.50048 6.42038C4.24034 6.49574 3.97131 6.53609 3.70051 6.54037C3.19117 6.54001 2.68928 6.41806 2.23656 6.18468C1.78384 5.95129 1.39337 5.6132 1.09762 5.19853C0.80187 4.78385 0.609381 4.30456 0.536158 3.80052C0.462935 3.29647 0.511092 2.78223 0.676627 2.30054L2.70855 4.33246L3.13253 3.90848L4.26848 2.77252L4.69247 2.34854L2.66055 0.316618C3.14224 0.151083 3.65648 0.102925 4.16053 0.176149C4.66457 0.249372 5.14386 0.441861 5.55854 0.737611C5.97321 1.03336 6.3113 1.42383 6.54469 1.87655C6.77807 2.32927 6.90002 2.83116 6.90038 3.3405C6.8961 3.6113 6.85575 3.88033 6.78039 4.14047L16.268 13.6441C16.417 13.794 16.5006 13.9967 16.5006 14.2081C16.5006 14.4194 16.417 14.6222 16.268 14.772ZM0.732625 13.6441C0.58363 13.794 0.5 13.9967 0.5 14.2081C0.5 14.4194 0.58363 14.6222 0.732625 14.772L1.86858 15.908C2.01846 16.057 2.22122 16.1406 2.43256 16.1406C2.6439 16.1406 2.84665 16.057 2.99654 15.908L7.37236 11.5402L5.10845 9.27626M14.9001 0.140625L11.7002 1.74056V3.3405L9.96426 5.07643L11.5642 6.67637L13.3001 4.94043H14.9001L16.5 1.74056L14.9001 0.140625Z"
                fill="#545454"
            />
        </svg>
    );
}

export function ImplementationIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M4.05556 8.14062H5.83333V9.74063H4.05556V8.14062ZM16.5 3.34063V14.5406C16.5 15.4286 15.7089 16.1406 14.7222 16.1406H2.27778C1.29111 16.1406 0.5 15.4206 0.5 14.5406V3.34063C0.5 2.46062 1.3 1.74063 2.27778 1.74063H3.16667V0.140625H4.94444V1.74063H12.0556V0.140625H13.8333V1.74063H14.7222C15.7089 1.74063 16.5 2.46062 16.5 3.34063ZM2.27778 4.94063H14.7222V3.34063H2.27778V4.94063ZM14.7222 14.5406V6.54063H2.27778V14.5406H14.7222ZM11.1667 9.74063H12.9444V8.14062H11.1667V9.74063ZM7.61111 9.74063H9.38889V8.14062H7.61111V9.74063Z"
                fill="#545454"
            />
        </svg>
    );
}

export function MeasureTypeIcon() {
    return (
        <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M2 0.140625C1.60218 0.140625 1.22064 0.309196 0.93934 0.609254C0.658035 0.909312 0.5 1.31628 0.5 1.74063V14.5406C0.5 14.965 0.658035 15.3719 0.93934 15.672C1.22064 15.9721 1.60218 16.1406 2 16.1406H11C11.3978 16.1406 11.7794 15.9721 12.0607 15.672C12.342 15.3719 12.5 14.965 12.5 14.5406V4.94063L8 0.140625H2ZM2 1.74063H7.25V5.74062H11V14.5406H2V1.74063ZM3.5 8.14062V9.74063H9.5V8.14062H3.5ZM3.5 11.3406V12.9406H7.25V11.3406H3.5Z"
                fill="#545454"
            />
        </svg>
    );
}

export function AIChatIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 80">
            <path
                d="M90 40c0 16.57-17.91 30-40 30c-5.23 0-10.24-.8-14.97-2.27L20 75V62.17C12.16 56.61 7.5 48.77 7.5 40C7.5 23.43 25.41 10 47.5 10S90 23.43 90 40z"
                fill="none"
                stroke="white"
                strokeWidth="5"
            />
            <text x="35" y="50" fontFamily="DMSans medium" fontSize="24" fontWeight="bold" fill="white">
                AI
            </text>
        </svg>
    );
}

export function BrainIcon({ color = "currentColor" }: { color?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
            <path d="M16 16c-1.33 1.33-3.08 2-5 2-1.92 0-3.67-.67-5-2" />
            <path d="M8 8c1.33-1.33 3.08-2 5-2 1.92 0 3.67.67 5 2" />
            <path d="M12 20v-4M12 4v4" />
            <path d="M4 12h4M16 12h4" />
        </svg>
    );
}

export function SubmitIcon() {
    return (
        <svg>
            <path d="M9.79883 18.5894C14.6216 18.5894 18.5894 14.6216 18.5894 9.79883C18.5894 4.96777 14.6216 1 9.79053 1C4.95947 1 1 4.96777 1 9.79883C1 14.6216 4.96777 18.5894 9.79883 18.5894ZM9.79883 14.3062C9.20947 14.3062 8.76953 13.9077 8.76953 13.3433V9.69922L8.86914 8.00586L8.25488 8.84424L7.3916 9.81543C7.23389 10.0063 6.98486 10.1143 6.72754 10.1143C6.21289 10.1143 5.84766 9.75732 5.84766 9.25928C5.84766 8.99365 5.92236 8.79443 6.12158 8.58691L8.96045 5.61523C9.19287 5.35791 9.4585 5.2417 9.79883 5.2417C10.1309 5.2417 10.4048 5.36621 10.6372 5.61523L13.4761 8.58691C13.667 8.79443 13.75 8.99365 13.75 9.25928C13.75 9.75732 13.3848 10.1143 12.8618 10.1143C12.6128 10.1143 12.3638 10.0063 12.2061 9.81543L11.3428 8.86914L10.7202 7.99756L10.8281 9.69922V13.3433C10.8281 13.9077 10.3799 14.3062 9.79883 14.3062Z" />
        </svg>
    );
}

export function UploadIcon({ style }: { style?: CSSProperties }) {
    return (
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
            <path
                d="M5.54529 10.2686V9.66859H4.94529H2.23954L8.061 3.84714L13.8824 9.66859H11.1767H10.5767V10.2686V15.9H5.54529V10.2686ZM14.731 1.36005H1.39102V1.1H14.731V1.36005Z"
                stroke="black"
                strokeWidth="1.2"
            />
        </svg>
    );
}

export function RedirectIcon({ style }: { style?: CSSProperties }) {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
            <path
                d="M6 2H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V10M9.5 2H14M14 2V6.5M14 2L7 9"
                stroke="black"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export function AIGenerate({ color = "white" }: { color?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20">
            <path d="M0 0h24v24H0z" fill="none" />
            <path
                d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
                fill={color}
            />
        </svg>
    );
}

interface ToolIconProps {
    color?: string;
}

export function ToolIcon({ color = "white" }: ToolIconProps) {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M19.7108 18.29L18.2908 19.71C18.1034 19.8962 17.85 20.0008 17.5858 20.0008C17.3216 20.0008 17.0682 19.8962 16.8808 19.71L5.00079 7.85C4.6756 7.94421 4.33931 7.99465 4.00079 8C3.3641 7.99955 2.73671 7.84711 2.17078 7.55536C1.60486 7.26362 1.11676 6.84099 0.747055 6.32263C0.377352 5.80426 0.136732 5.20513 0.0451992 4.57505C-0.0463335 3.94496 0.013866 3.30213 0.220793 2.7L2.76079 5.24L3.29079 4.71L4.71079 3.29L5.24079 2.76L2.70079 0.22C3.30293 0.0130733 3.94576 -0.0471263 4.57584 0.0444064C5.20592 0.135939 5.80505 0.376559 6.32342 0.746262C6.84179 1.11596 7.26441 1.60407 7.55616 2.16999C7.8479 2.73591 8.00034 3.3633 8.00079 4C7.99544 4.33852 7.945 4.67481 7.85079 5L19.7108 16.88C19.897 17.0674 20.0016 17.3208 20.0016 17.585C20.0016 17.8492 19.897 18.1026 19.7108 18.29ZM0.290793 16.88C0.104542 17.0674 0 17.3208 0 17.585C0 17.8492 0.104542 18.1026 0.290793 18.29L1.71079 19.71C1.89816 19.8962 2.15161 20.0008 2.41579 20.0008C2.67998 20.0008 2.93343 19.8962 3.12079 19.71L8.59079 14.25L5.76079 11.42M18.0008 0L14.0008 2V4L11.8308 6.17L13.8308 8.17L16.0008 6H18.0008L20.0008 2L18.0008 0Z"
                fill={color}
            />
        </svg>
    );
}

interface RiskExposureIconProps {
    color?: string;
}

export function RiskExposureIcon({ color = "white" }: RiskExposureIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
                d="M12 2L3 6V11C3 16.52 7.2 21.74 12 23C16.8 21.74 21 16.52 21 11V6L12 2Z"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <rect x="8" y="10" width="2" height="6" fill={color} />
            <rect x="11" y="8" width="2" height="8" fill={color} />
            <rect x="14" y="12" width="2" height="4" fill={color} />
        </svg>
    );
}

export function CashFlowIcon({ color = "black" }: { color?: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 18h16v2H4v-2ZM4 13h16v2H4v-2ZM4 8h16v2H4V8Z" fill={color} />
            <path d="M12 2v4m0 0-2-2m2 2 2-2" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

export function InfoOutlinedIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="23" viewBox="0 0 22 23" fill="none">
            <path
                d="M10.0833 7.25643H11.9167V9.08976H10.0833V7.25643ZM10.0833 10.9231H11.9167V16.4231H10.0833V10.9231ZM11 2.6731C5.94001 2.6731 1.83334 6.77976 1.83334 11.8398C1.83334 16.8998 5.94001 21.0064 11 21.0064C16.06 21.0064 20.1667 16.8998 20.1667 11.8398C20.1667 6.77976 16.06 2.6731 11 2.6731ZM11 19.1731C6.95751 19.1731 3.66668 15.8823 3.66668 11.8398C3.66668 7.79726 6.95751 4.50643 11 4.50643C15.0425 4.50643 18.3333 7.79726 18.3333 11.8398C18.3333 15.8823 15.0425 19.1731 11 19.1731Z"
                fill="#EF6C00"
            />
        </svg>
    );
}

interface BaselineIconProps {
    color?: string;
}

export function BaselineIcon({ color = "white" }: BaselineIconProps) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
                d="M12 2L3 6V11C3 16.52 7.2 21.74 12 23C16.8 21.74 21 16.52 21 11V6L12 2Z"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <rect x="8" y="10" width="2" height="6" fill={color} />
            <rect x="11" y="8" width="2" height="8" fill={color} />
            <rect x="14" y="12" width="2" height="4" fill={color} />
        </svg>
    );
}

interface DecarbonizationIconProps {
    color?: string;
}

export function DecarbonizationIcon({ color = "black" }: DecarbonizationIconProps) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M3 17H5V21H3V17ZM7 13H9V21H7V13ZM11 9H13V21H11V9ZM15 5H17V21H15V5ZM19 1H21V21H19V1Z"
                fill={color}
            />
        </svg>
    );
}

export function EnergyIntensityIcon({ color = "black" }: { color?: string }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M4 18h2v-5H4v5Zm4 0h2v-9H8v9Zm4 0h2V6h-2v12Zm4 0h2v-7h-2v7Zm4 0h2v-4h-2v4Z" fill={color} />
            <path d="M12 2L9 6h3v4l3-4h-3V2Z" fill={color} />
        </svg>
    );
}

export function PolicyIcon({ color = "black" }: { color?: string }) {
    return (
        <svg width="20" height="20" viewBox="-2 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M14.443 4.445a1.615 1.615 0 0 1-1.613 1.614h-.506v8.396a1.615 1.615 0 0 1-1.613 1.613H2.17a1.613 1.613 0 1 1 0-3.227h.505V4.445A1.615 1.615 0 0 1 4.289 2.83h8.54a1.615 1.615 0 0 1 1.614 1.614zM2.17 14.96h7.007a1.612 1.612 0 0 1 0-1.01H2.172a.505.505 0 0 0 0 1.01zm9.045-10.515a1.62 1.62 0 0 1 .08-.505H4.29a.5.5 0 0 0-.31.107l-.002.001a.5.5 0 0 0-.193.397v8.396h6.337a.61.61 0 0 1 .6.467.632.632 0 0 1-.251.702.505.505 0 1 0 .746.445zm-.86 1.438h-5.76V6.99h5.76zm0 2.26h-5.76V9.25h5.76zm0 2.26h-5.76v1.108h5.76zm2.979-5.958a.506.506 0 0 0-.505-.505.496.496 0 0 0-.31.107h-.002a.501.501 0 0 0-.194.398v.505h.506a.506.506 0 0 0 .505-.505z"
                fill={color}
            />
        </svg>
    );
}

export function SubmittedIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="opacity-60"
        >
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.7893 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" />
            <path d="M14 2V8H20" />
        </svg>
    );
}

export function ValidatedIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="green"
            strokeWidth="2"
            className="opacity-70"
        >
            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4900 2.02168 11.3363C2.16356 9.18266 2.99721 7.13316 4.39828 5.49618C5.79935 3.85919 7.69279 2.71794 9.79619 2.24408C11.8996 1.77023 14.1003 1.98903 16.07 2.86" />
            <path d="M22 4L12 14.01L9 11.01" />
        </svg>
    );
}

export function InvalidatedIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" className="opacity-70">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
    );
}

export function DragIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="6" width="16" height="2" rx="1" fill="currentColor" />
            <rect x="4" y="11" width="16" height="2" rx="1" fill="currentColor" />
            <rect x="4" y="16" width="16" height="2" rx="1" fill="currentColor" />
        </svg>
    );
}

export function DocumentIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2H14L20 8V22H6V2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" />
            <line x1="8" y1="16" x2="16" y2="16" stroke="currentColor" strokeWidth="2" />
        </svg>
    );
}

export function BulletListIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3-1h9a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1zm0 4h9a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1zm0 4h9a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1zM2 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
        </svg>
    );
}

export function OrderedListIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 0 1H2v.5h.5a.5.5 0 0 1 0 1H2v.5h.5a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5v-3zm3 .5h9a.5.5 0 0 1 0 1H4a.5.5 0 0 1 0-1zm0 4h9a.5.5 0 0 1 0 1H4a.5.5 0 0 1 0-1zm0 4h9a.5.5 0 0 1 0 1H4a.5.5 0 0 1 0-1zM1 7a.5.5 0 0 1 .5-.5h1c.276 0 .5.193.5.431v.139c0 .203-.14.373-.333.43L2.5 7.75H3a.5.5 0 0 1 0 1h-1a.5.5 0 0 1-.5-.5v-.139c0-.203.14-.373.333-.43L2.5 7.5H2V7zm0 4.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v.5H2.5v.25H3a.5.5 0 0 1 0 1H2a.5.5 0 0 1-.5-.5V12a.5.5 0 0 1 .5-.5h.5V11H1.5a.5.5 0 0 1 0-1H2z" />
        </svg>
    );
}
